import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';

/**
 * Screen context for the AI copilot.
 * Pages report their visible data summary, and the copilot reads it
 * to understand what's on screen before responding.
 *
 * UI actions are discovered via an intelligent screen reader approach:
 * 1. Explicit `data-ai-action` / `data-ai-field` attributes (precise, for complex cases)
 * 2. Automatic DOM scanning for ALL interactive elements (buttons, inputs, selects)
 *    using visible text, aria-labels, and semantic HTML for identification
 *
 * The AI can target elements by either:
 * - Their explicit data-ai-action/data-ai-field identifier
 * - Their visible text label (e.g., "Save", "Cancel", "Nauja SF")
 * - Their associated label text, aria-label, placeholder, or name attribute
 */

export interface ScreenAction {
  /** Unique action ID (from data-ai-action attribute or auto-generated from text) */
  id: string;
  /** Human-readable label (button text, aria-label, or title) */
  label: string;
  /** CSS selector hint for re-discovery (useful for auto-discovered elements) */
  selector?: string;
  /** Whether this was an explicit data-ai-action or auto-discovered */
  explicit: boolean;
}

export interface ScreenFormField {
  /** Field name (from data-ai-field attribute or auto-generated from label) */
  name: string;
  /** Human-readable label (from associated label or aria-label) */
  label: string;
  /** Input type: text, number, select, date, etc. */
  type: string;
  /** Current value (string representation) */
  value: string;
  /** CSS selector hint for re-discovery (useful for auto-discovered elements) */
  selector?: string;
  /** Whether this was an explicit data-ai-field or auto-discovered */
  explicit: boolean;
}

export interface TableDataSnapshot {
  /** Column headers */
  headers: string[];
  /** Row data -- each row is an array of cell text values */
  rows: string[][];
  /** Total number of rows in the table (may be larger than rows.length) */
  totalRows?: number;
}

export interface ActiveRecordSnapshot {
  /** Type of record (e.g., "bank-operation", "journal-entry", "invoice") */
  type?: string;
  /** Field name to value mapping extracted from the detail panel */
  fields: Record<string, string>;
  /** Raw text content of the detail panel (fallback when structured extraction fails) */
  rawData?: string;
}

export interface ScreenData {
  /** Human-readable description of current page content */
  summary: string;
  /** Number of visible records (e.g., invoices count) */
  recordCount?: number;
  /** Active filters (e.g., date range, search terms) */
  filters?: Record<string, string>;
  /** Data preview -- first few items for context (keys only, no full documents per privacy policy) */
  preview?: string[];
  /** Visible table data -- headers + first N rows of actual cell values */
  tableData?: TableDataSnapshot[];
  /** Currently selected/active record details */
  activeRecord?: ActiveRecordSnapshot;
  /** Available actions -- auto-discovered by DOM scanner (explicit + semantic) */
  actions?: ScreenAction[];
  /** Form fields -- auto-discovered by DOM scanner (explicit + semantic) */
  formFields?: ScreenFormField[];
  /** Timestamp when this context was set */
  updatedAt?: number;
  /** Available workspaces for direct switching via activate_workspace action */
  workspaces?: { id: string; name: string; isActive: boolean; companyName?: string | null }[];
  /** Currently active workspace */
  activeWorkspace?: { id: string; name: string } | null;
}

// ──────────────────────────────────────────────
// Helper functions for intelligent element discovery
// ──────────────────────────────────────────────

/**
 * Check if a label is too generic to be useful for AI targeting.
 * Filters out SVG path data, very short noise, icon-only buttons.
 */
function isGenericLabel(label: string): boolean {
  const trimmed = label.trim();
  if (trimmed.length === 0) return true;
  // Skip single non-letter characters (icon-only buttons like "x", ">", arrows)
  if (trimmed.length <= 1 && !/[a-zA-ZąčęėįšųūžĄČĘĖĮŠŲŪŽ]/.test(trimmed)) return true;
  // Skip SVG path data (starts with M and has many coordinates)
  if (trimmed.startsWith('M') && (trimmed.includes('L') || trimmed.includes('C')) && trimmed.length > 20) return true;
  // Skip strings that are just numbers or symbols
  if (/^[\d\s\-\.\,]+$/.test(trimmed) && trimmed.length <= 3) return true;
  return false;
}

/**
 * Convert a human-readable label to a stable slug ID.
 * "Save Invoice" -> "save_invoice", "Nauja SF" -> "nauja_sf"
 */
function labelToId(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9ąčęėįšųūža-z]+/gi, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 40);
}

/**
 * Build a stable CSS selector for an element so it can be re-found later.
 * Prefers: data-ai-* > id > name > tag:nth-of-type among siblings.
 */
function buildSelector(el: Element): string {
  if (el.id) return `#${CSS.escape(el.id)}`;
  if (el.hasAttribute('data-ai-action')) {
    return `[data-ai-action="${CSS.escape(el.getAttribute('data-ai-action')!)}"]`;
  }
  if (el.hasAttribute('data-ai-field')) {
    return `[data-ai-field="${CSS.escape(el.getAttribute('data-ai-field')!)}"]`;
  }
  const name = el.getAttribute('name');
  if (name) {
    const tag = el.tagName.toLowerCase();
    return `${tag}[name="${CSS.escape(name)}"]`;
  }
  const tag = el.tagName.toLowerCase();
  const parent = el.parentElement;
  if (parent) {
    const siblings = Array.from(parent.children).filter(c => c.tagName === el.tagName);
    if (siblings.length > 1) {
      const index = siblings.indexOf(el) + 1;
      return `${tag}:nth-of-type(${index})`;
    }
  }
  return tag;
}

/**
 * Case-insensitive fuzzy match for labels.
 * Supports: exact match, substring match (both directions), word-boundary match.
 */
function labelMatches(label: string, target: string): boolean {
  if (!label || !target) return false;
  const normLabel = label.toLowerCase().trim();
  const normTarget = target.toLowerCase().trim();
  if (normLabel === normTarget) return true;
  // label contains target (e.g., target "save" matches label "Save Invoice")
  if (normLabel.includes(normTarget) && normTarget.length >= 2) return true;
  // target contains label (e.g., target "save invoice" matches label "Save")
  if (normTarget.includes(normLabel) && normLabel.length >= 3) return true;
  return false;
}

/**
 * Find the human-readable label for a form field by checking:
 * 1. Wrapping <label> element
 * 2. Associated <label for="id"> element
 * 3. Previous sibling <label> or text
 * 4. aria-label, title, placeholder attributes
 */
function findFieldLabel(htmlEl: HTMLElement): string | null {
  // Try wrapping <label>
  const parentLabel = htmlEl.closest('label');
  if (parentLabel) {
    const labelText = parentLabel.textContent?.trim()?.substring(0, 50);
    if (labelText) return labelText;
  }

  // Try <label for="id">
  const inputId = htmlEl.id;
  if (inputId) {
    const associatedLabel = document.querySelector(`label[for="${CSS.escape(inputId)}"]`);
    const labelText = associatedLabel?.textContent?.trim()?.substring(0, 50);
    if (labelText) return labelText;
  }

  // Try preceding sibling <label> or text
  const prev = htmlEl.previousElementSibling;
  if (prev?.tagName === 'LABEL') {
    const labelText = prev.textContent?.trim()?.substring(0, 50);
    if (labelText) return labelText;
  }

  return null;
}

/**
 * Determine the type string for a form element.
 */
function getFieldType(htmlEl: HTMLElement): string {
  const tagName = htmlEl.tagName.toLowerCase();
  if (tagName === 'select') return 'select';
  if (tagName === 'textarea') return 'textarea';
  if (htmlEl instanceof HTMLInputElement) {
    return htmlEl.type || 'text';
  }
  return 'text';
}

// ──────────────────────────────────────────────
// DOM UI Actuator -- click by ID or label
// ──────────────────────────────────────────────

/**
 * Execute an AI action by finding and clicking a DOM element.
 * Matching strategy (in order):
 * 1. Exact match on data-ai-action attribute (backward compatible)
 * 2. Match by visible button text (case-insensitive, trimmed)
 * 3. Match by aria-label or title attribute (case-insensitive)
 * 4. Partial text match (contains, for longer button labels)
 */
export function executeAIAction(actionId: string): boolean {
  // Strategy 1: Exact data-ai-action match (backward compatible)
  const explicitEl = document.querySelector(`[data-ai-action="${CSS.escape(actionId)}"]`) as HTMLElement | null;
  if (explicitEl) {
    explicitEl.click();
    return true;
  }

  // Strategy 2-4: Text-based matching on all buttons and clickable elements
  const normalizedName = actionId.toLowerCase().trim();

  // Collect all clickable elements: buttons, links, elements with role="button"
  const clickables = document.querySelectorAll('button, [role="button"], a[href], [tabindex="0"], input[type="button"], input[type="submit"]');

  // Strategy 2: Exact visible text match
  for (const el of clickables) {
    const text = (el.textContent || '').trim().toLowerCase();
    if (text && text === normalizedName) {
      (el as HTMLElement).click();
      return true;
    }
  }

  // Strategy 3: aria-label or title match
  for (const el of clickables) {
    const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase().trim();
    const title = (el.getAttribute('title') || '').toLowerCase().trim();
    if ((ariaLabel && ariaLabel === normalizedName) || (title && title === normalizedName)) {
      (el as HTMLElement).click();
      return true;
    }
  }

  // Strategy 4: Partial text match (visible text contains the target, or target contains visible text)
  for (const el of clickables) {
    const text = (el.textContent || '').trim().toLowerCase();
    if (text && text.length >= 2 && (text.includes(normalizedName) || normalizedName.includes(text))) {
      (el as HTMLElement).click();
      return true;
    }
  }

  console.warn('AI action element not found by ID or text:', actionId);
  return false;
}

// ──────────────────────────────────────────────
// DOM Scanners -- discover actions and fields
// ──────────────────────────────────────────────

/**
 * Scan the DOM for all interactive elements and return their IDs and labels.
 *
 * Two-phase discovery:
 * Phase 1: Elements with explicit data-ai-action attribute (precise targeting)
 * Phase 2: ALL other buttons/links/clickable elements (auto-discovered by text/label)
 *
 * For auto-discovered elements, the ID is derived from the visible text
 * (slugified, e.g., "Save" -> "save", "Nauja SF" -> "nauja_sf").
 */
export function scanDOMActions(): ScreenAction[] {
  const actions: ScreenAction[] = [];
  const seen = new Set<string>();

  // Phase 1: Explicit data-ai-action elements
  const explicitElements = document.querySelectorAll('[data-ai-action]');
  explicitElements.forEach((el) => {
    const id = el.getAttribute('data-ai-action');
    if (!id) return;
    // Get label from button text, aria-label, or title
    const label =
      el.getAttribute('aria-label') ||
      el.getAttribute('title') ||
      el.textContent?.trim()?.substring(0, 50) ||
      id;
    seen.add(id.toLowerCase());
    actions.push({ id, label, explicit: true, selector: buildSelector(el) });
  });

  // Phase 2: Auto-discover all other interactive elements
  const clickables = document.querySelectorAll('button, [role="button"], a[href], input[type="button"], input[type="submit"]');
  clickables.forEach((el) => {
    // Skip if already has data-ai-action
    if (el.hasAttribute('data-ai-action')) return;

    // Skip hidden or disabled elements
    if ((el as HTMLElement).hidden || (el as HTMLButtonElement).disabled) return;

    // Get label from aria-label, title, value (for inputs), or text content
    const label =
      el.getAttribute('aria-label') ||
      el.getAttribute('title') ||
      (el instanceof HTMLInputElement ? el.value : null) ||
      el.textContent?.trim()?.substring(0, 50);

    if (!label) return; // Skip elements without any identifiable text

    // Filter out generic/unhelpful labels (icon-only buttons, SVG paths)
    if (isGenericLabel(label)) return;

    // Generate slug from label text
    const id = labelToId(label);

    if (!id || seen.has(id)) return; // Skip empty or duplicate slugs
    seen.add(id);
    actions.push({ id, label, explicit: false, selector: buildSelector(el) });
  });

  return actions;
}

/**
 * Scan the DOM for all form fields and return their metadata.
 *
 * Two-phase discovery:
 * Phase 1: Elements with explicit data-ai-field attribute (precise targeting)
 * Phase 2: ALL other visible input/select/textarea elements (auto-discovered by label)
 *
 * For auto-discovered fields, the name is derived from the associated label text.
 */
export function scanDOMFields(): ScreenFormField[] {
  const fields: ScreenFormField[] = [];
  const seen = new Set<string>();

  // Phase 1: Explicit data-ai-field elements
  const explicitElements = document.querySelectorAll('[data-ai-field]');
  explicitElements.forEach((el) => {
    const name = el.getAttribute('data-ai-field');
    if (!name) return;

    const htmlEl = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

    // Get label from associated <label>, aria-label, title, or placeholder
    let label = findFieldLabel(htmlEl) ||
      htmlEl.getAttribute('aria-label') ||
      htmlEl.getAttribute('title') ||
      (htmlEl as HTMLInputElement).placeholder ||
      name;

    // Determine type
    const type = getFieldType(htmlEl);

    // Get current value
    const value = htmlEl.value ?? '';

    seen.add(name.toLowerCase());
    fields.push({ name, label, type, value, explicit: true, selector: buildSelector(el) });
  });

  // Phase 2: Auto-discover all other visible form inputs
  const inputs = document.querySelectorAll('input, select, textarea');
  inputs.forEach((el) => {
    // Skip if already has data-ai-field
    if (el.hasAttribute('data-ai-field')) return;

    const htmlEl = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

    // Skip hidden, submit, button, reset, and disabled inputs
    if (htmlEl instanceof HTMLInputElement) {
      const skipTypes = ['hidden', 'submit', 'button', 'reset', 'file', 'image'];
      if (skipTypes.includes(htmlEl.type)) return;
    }
    if ((htmlEl as HTMLInputElement).disabled) return;
    if (htmlEl.hidden) return;

    // Skip password fields for privacy
    if (htmlEl instanceof HTMLInputElement && htmlEl.type === 'password') return;

    // Try to get a label
    const label = findFieldLabel(htmlEl) ||
      htmlEl.getAttribute('aria-label') ||
      htmlEl.getAttribute('title') ||
      (htmlEl instanceof HTMLInputElement ? htmlEl.placeholder : null) ||
      htmlEl.getAttribute('name') ||
      null;

    if (!label) return; // Skip fields without any identifiable label

    // Generate slug from label text
    const name = labelToId(label);

    if (!name || seen.has(name)) return;
    seen.add(name);

    const type = getFieldType(htmlEl);
    const value = htmlEl.value ?? '';

    fields.push({ name, label, type, value, explicit: false, selector: buildSelector(htmlEl) });
  });

  return fields;
}

// ──────────────────────────────────────────────
// DOM UI Actuator -- fill fields by name or label
// ──────────────────────────────────────────────

/**
 * Set the value of a form field.
 * Matching strategy (in order):
 * 1. Exact match on data-ai-field attribute (backward compatible)
 * 2. Match by field name/id attribute (case-insensitive)
 * 3. Match by associated label text (case-insensitive)
 * 4. Match by aria-label or title (case-insensitive, fuzzy)
 * 5. Match by placeholder (case-insensitive)
 * 6. Fuzzy partial match on label text
 *
 * Uses React-compatible value setter pattern to trigger onChange handlers.
 * Returns true if the field was found and updated.
 */
export function executeAIFill(fieldName: string, value: string): boolean {
  // Strategy 1: Exact data-ai-field match (backward compatible)
  let el = document.querySelector(`[data-ai-field="${CSS.escape(fieldName)}"]`) as
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement
    | null;

  if (el) {
    return setFieldValue(el, value);
  }

  // Strategy 2-6: Text-based matching on all form fields
  const normalizedName = fieldName.toLowerCase().trim();
  const inputs = document.querySelectorAll('input, select, textarea');

  for (const input of inputs) {
    const htmlEl = input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

    // Skip hidden/submit/button types and disabled
    if (htmlEl instanceof HTMLInputElement) {
      const skipTypes = ['hidden', 'submit', 'button', 'reset', 'file', 'image'];
      if (skipTypes.includes(htmlEl.type)) continue;
    }
    if ((htmlEl as HTMLInputElement).disabled || htmlEl.hidden) continue;

    // Strategy 2: Exact match on name or id attribute
    const name = htmlEl.getAttribute('name')?.toLowerCase().trim();
    const id = htmlEl.getAttribute('id')?.toLowerCase().trim();
    if (name === normalizedName || id === normalizedName) {
      return setFieldValue(htmlEl, value);
    }

    // Strategy 3: Exact match on associated label text
    const labelText = findFieldLabel(htmlEl);
    if (labelText && labelText.toLowerCase().trim() === normalizedName) {
      return setFieldValue(htmlEl, value);
    }

    // Strategy 4: aria-label or title (case-insensitive, fuzzy)
    const ariaLabel = htmlEl.getAttribute('aria-label');
    const title = htmlEl.getAttribute('title');
    if ((ariaLabel && labelMatches(ariaLabel, fieldName)) ||
        (title && labelMatches(title, fieldName))) {
      return setFieldValue(htmlEl, value);
    }

    // Strategy 5: placeholder exact match
    const placeholder = (htmlEl instanceof HTMLInputElement || htmlEl instanceof HTMLTextAreaElement)
      ? htmlEl.placeholder
      : null;
    if (placeholder && placeholder.toLowerCase().trim() === normalizedName) {
      return setFieldValue(htmlEl, value);
    }

    // Strategy 6: Fuzzy partial match on label text
    if (labelText && normalizedName.length >= 3 && labelText.toLowerCase().includes(normalizedName)) {
      return setFieldValue(htmlEl, value);
    }
  }

  console.warn('AI fill: field not found by name or label:', fieldName);
  return false;
}

/**
 * Internal helper: set a field value using React-compatible native setter.
 */
function setFieldValue(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, value: string): boolean {
  const prototype = el instanceof HTMLSelectElement
    ? HTMLSelectElement.prototype
    : el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;

  const nativeSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  if (nativeSetter) {
    nativeSetter.call(el, value);
  } else {
    el.value = value;
  }

  // Dispatch events that React listens to
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));

  return true;
}

// ──────────────────────────────────────────────
// DOM Scanners -- table data and active record
// ──────────────────────────────────────────────

const MAX_TABLE_ROWS = 10;
const MAX_CELL_LENGTH = 100;
const MAX_TABLES = 3;

/**
 * Check if an element is visually hidden (display:none, visibility:hidden, zero dimensions).
 */
function isElementHidden(el: Element): boolean {
  const htmlEl = el as HTMLElement;
  if (!htmlEl) return true;
  const style = window.getComputedStyle(htmlEl);
  return (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0' ||
    htmlEl.offsetWidth === 0 ||
    htmlEl.offsetHeight === 0
  );
}

/**
 * Scan visible tables and data grids on the page, capturing headers and first N rows of actual data.
 * Looks for <table> elements and div structures with role="grid" or data-table attributes.
 * Limits output to MAX_TABLES tables, MAX_TABLE_ROWS rows each, MAX_CELL_LENGTH chars per cell.
 */
export function scanDOMTableData(): TableDataSnapshot[] {
  const results: TableDataSnapshot[] = [];
  const seenTables = new Set<Element>();

  // Phase 1: Native <table> elements
  const tables = document.querySelectorAll('table');
  for (const table of tables) {
    if (results.length >= MAX_TABLES) break;
    if (seenTables.has(table)) continue;
    if (isElementHidden(table)) continue;

    // Skip tiny utility tables (e.g., date pickers, layout tables with <3 columns)
    const thCount = table.querySelectorAll('thead th, tr:first-child th').length;
    const tdRows = table.querySelectorAll('tbody tr, tr');
    if (thCount < 2 && tdRows.length < 2) continue;

    seenTables.add(table);
    const snapshot = extractTableFromElement(table);
    if (snapshot && snapshot.rows.length > 0) {
      results.push(snapshot);
    }
  }

  // Phase 2: Grid-like div structures (role="grid", role="table", data-table, data-grid)
  const gridSelectors = [
    '[role="grid"]',
    '[role="table"]',
    '[data-table]',
    '[data-grid]',
  ];
  const grids = document.querySelectorAll(gridSelectors.join(', '));
  for (const grid of grids) {
    if (results.length >= MAX_TABLES) break;
    if (seenTables.has(grid)) continue;
    if (isElementHidden(grid)) continue;

    seenTables.add(grid);
    const snapshot = extractGridFromElement(grid);
    if (snapshot && snapshot.rows.length > 0) {
      results.push(snapshot);
    }
  }

  // Phase 3: Common accounting table patterns -- div-based tables with header/row structure
  // Many React table libraries render as divs with classes like "table", "data-table", etc.
  const divTables = document.querySelectorAll('.table, [class*="data-table"], [class*="DataTable"]');
  for (const divTable of divTables) {
    if (results.length >= MAX_TABLES) break;
    if (seenTables.has(divTable)) continue;
    if (isElementHidden(divTable)) continue;
    // Skip if it wraps a real <table> we already captured
    if (divTable.querySelector('table') && seenTables.has(divTable.querySelector('table')!)) continue;

    seenTables.add(divTable);
    const snapshot = extractGridFromElement(divTable);
    if (snapshot && snapshot.rows.length > 0) {
      results.push(snapshot);
    }
  }

  return results;
}

/**
 * Extract table data from a native <table> element.
 */
function extractTableFromElement(table: Element): TableDataSnapshot | null {
  const headers: string[] = [];

  // Try to get headers from <thead> first
  const theadThs = table.querySelectorAll('thead th');
  if (theadThs.length > 0) {
    theadThs.forEach(th => {
      const text = th.textContent?.trim() || '';
      if (text) headers.push(text.substring(0, MAX_CELL_LENGTH));
    });
  } else {
    // Fall back to first row's th/td elements as headers
    const firstRow = table.querySelector('tr');
    if (firstRow) {
      firstRow.querySelectorAll('th, td').forEach(cell => {
        const text = cell.textContent?.trim() || '';
        if (text) headers.push(text.substring(0, MAX_CELL_LENGTH));
      });
    }
  }

  // Get data rows from <tbody>, or all <tr> if no <tbody>
  const rowContainer = table.querySelector('tbody') || table;
  const rows = rowContainer.querySelectorAll('tr');
  const dataRows: string[][] = [];

  for (let i = 0; i < rows.length && dataRows.length < MAX_TABLE_ROWS; i++) {
    const row = rows[i] as HTMLTableRowElement | undefined;
    if (!row || isElementHidden(row)) continue;

    // Skip the header row if we already have thead headers
    if (theadThs.length > 0 && i === 0 && row.closest('thead')) continue;
    // Also skip if the first row has th elements and we already got headers from thead
    if (i === 0 && theadThs.length > 0 && row.querySelector('th')) continue;

    const cells: string[] = [];
    row.querySelectorAll('td, th').forEach(cell => {
      const text = cell.textContent?.trim() || '';
      cells.push(text.substring(0, MAX_CELL_LENGTH));
    });

    if (cells.length > 0) {
      dataRows.push(cells);
    }
  }

  if (headers.length === 0 && dataRows.length === 0) return null;

  const totalRows = rowContainer.querySelectorAll('tr').length;

  return {
    headers,
    rows: dataRows,
    totalRows: totalRows > MAX_TABLE_ROWS ? totalRows : undefined,
  };
}

/**
 * Extract table data from a div-based grid structure.
 * Looks for patterns like: header row with role="row"/"columnheader", data rows with role="row"/"gridcell".
 * Also handles common patterns: first child row as header, subsequent rows as data.
 */
function extractGridFromElement(grid: Element): TableDataSnapshot | null {
  const headers: string[] = [];
  const dataRows: string[][] = [];

  // Strategy 1: Look for ARIA-based grid with role="row" and role="columnheader"/"gridcell"
  const allRows = grid.querySelectorAll('[role="row"], [role="rowgroup"]');
  let headerRow: Element | null = null;

  for (const row of allRows) {
    const hasColumnHeaders = row.querySelectorAll('[role="columnheader"]').length > 0;
    if (hasColumnHeaders && !headerRow) {
      headerRow = row;
      row.querySelectorAll('[role="columnheader"]').forEach(cell => {
        const text = cell.textContent?.trim() || '';
        if (text) headers.push(text.substring(0, MAX_CELL_LENGTH));
      });
    }
  }

  for (const row of allRows) {
    if (row === headerRow) continue;
    if (dataRows.length >= MAX_TABLE_ROWS) break;
    if (isElementHidden(row)) continue;

    const cells: string[] = [];
    row.querySelectorAll('[role="gridcell"], [role="cell"], td').forEach(cell => {
      const text = cell.textContent?.trim() || '';
      cells.push(text.substring(0, MAX_CELL_LENGTH));
    });
    if (cells.length > 0) dataRows.push(cells);
  }

  // Strategy 2: If no ARIA roles found, try child div patterns
  if (headers.length === 0 && dataRows.length === 0) {
    // Look for a header div followed by data rows
    const children = Array.from(grid.children);
    let foundHeader = false;

    for (const child of children) {
      if (isElementHidden(child)) continue;

      // Detect header: first row that contains multiple text elements, often with specific styling
      const subChildren = child.children;
      if (!foundHeader && subChildren.length >= 2) {
        // Check if this looks like a header row (bold text, smaller font, etc.)
        const textNodes = Array.from(subChildren).filter(c => c.textContent?.trim());
        if (textNodes.length >= 2) {
          foundHeader = true;
          textNodes.forEach(el => {
            const text = el.textContent?.trim() || '';
            if (text) headers.push(text.substring(0, MAX_CELL_LENGTH));
          });
          continue;
        }
      }

      if (foundHeader && dataRows.length < MAX_TABLE_ROWS) {
        const cells: string[] = [];
        Array.from(subChildren).forEach(el => {
          const text = el.textContent?.trim() || '';
          cells.push(text.substring(0, MAX_CELL_LENGTH));
        });
        if (cells.length > 0) dataRows.push(cells);
      }
    }
  }

  if (headers.length === 0 && dataRows.length === 0) return null;

  return {
    headers,
    rows: dataRows,
    totalRows: dataRows.length >= MAX_TABLE_ROWS ? undefined : undefined,
  };
}

/**
 * Scan for the currently selected/active record on the page.
 * Detects: selected table rows, open detail panels, modals with record data.
 * Returns structured field-value pairs if possible, falls back to raw text.
 */
export function scanActiveRecord(): ActiveRecordSnapshot | null {
  // Strategy 1: Look for a selected/highlighted table row
  const selectedRow = findSelectedTableRow();
  if (selectedRow) {
    return extractRecordFromRow(selectedRow);
  }

  // Strategy 2: Look for a detail panel or modal that is open
  const detailPanel = findOpenDetailPanel();
  if (detailPanel) {
    return extractRecordFromPanel(detailPanel);
  }

  return null;
}

/**
 * Find the currently selected/highlighted table row.
 * Checks for common selection CSS classes and aria-selected attribute.
 */
function findSelectedTableRow(): HTMLTableRowElement | HTMLDivElement | null {
  const selectionSelectors = [
    'tr.selected', 'tr.active', 'tr.bg-blue-50', 'tr.bg-primary-50',
    'tr.bg-indigo-50', 'tr.bg-blue-100', 'tr.bg-primary-100',
    'tr[class*="selected"]', 'tr[class*="active"]', 'tr[class*="highlight"]',
    'tr[aria-selected="true"]',
    '[role="row"].selected', '[role="row"].active', '[role="row"][aria-selected="true"]',
    '[role="row"][class*="selected"]', '[role="row"][class*="active"]',
    'div.selected', 'div.active', 'div.bg-blue-50', 'div.bg-primary-50',
  ];

  for (const selector of selectionSelectors) {
    const el = document.querySelector(selector);
    if (el && !isElementHidden(el)) {
      return el as HTMLTableRowElement | HTMLDivElement;
    }
  }

  return null;
}

/**
 * Extract record data from a selected table row.
 * Uses the table's column headers as field names.
 */
function extractRecordFromRow(row: HTMLTableRowElement | HTMLDivElement): ActiveRecordSnapshot | null {
  const table = row.closest('table') || row.closest('[role="grid"]') || row.closest('[role="table"]');
  const headers: string[] = [];

  // Get headers from the parent table
  if (table) {
    if (table.tagName === 'TABLE') {
      const ths = table.querySelectorAll('thead th');
      if (ths.length === 0) {
        const firstRow = table.querySelector('tr');
        firstRow?.querySelectorAll('th, td').forEach(th => {
          headers.push(th.textContent?.trim() || '');
        });
      } else {
        ths.forEach(th => {
          headers.push(th.textContent?.trim() || '');
        });
      }
    } else {
      // Grid structure
      const headerCells = table.querySelectorAll('[role="columnheader"]');
      headerCells.forEach(cell => {
        headers.push(cell.textContent?.trim() || '');
      });
    }
  }

  // Get cell values from the selected row
  const cells: string[] = [];
  const cellSelector = row.tagName === 'TR'
    ? 'td, th'
    : '[role="gridcell"], [role="cell"], td';
  row.querySelectorAll(cellSelector).forEach(cell => {
    const text = cell.textContent?.trim() || '';
    cells.push(text.substring(0, MAX_CELL_LENGTH));
  });

  if (cells.length === 0) return null;

  // Build field map from headers + cell values
  const fields: Record<string, string> = {};
  const startIdx = headers.length > 0 ? Math.min(cells.length, headers.length) : 0;

  for (let i = 0; i < startIdx; i++) {
    const key = headers[i]?.trim();
    if (key) {
      fields[key] = cells[i] || '';
    }
  }

  // If no headers matched, use positional keys
  if (Object.keys(fields).length === 0) {
    cells.forEach((val, idx) => {
      fields[`col_${idx + 1}`] = val;
    });
  }

  // Infer record type from the row's context (closest section, table caption, etc.)
  let type: string | undefined;
  const section = row.closest('section, [data-page], main');
  if (section) {
    const pageAttr = section.getAttribute('data-page');
    const heading = section.querySelector('h1, h2, h3');
    if (pageAttr) type = pageAttr;
    else if (heading) type = heading.textContent?.trim()?.substring(0, 30) || undefined;
  }

  return {
    type,
    fields,
  };
}

/**
 * Find an open detail panel, modal, or drawer showing record details.
 */
function findOpenDetailPanel(): Element | null {
  const panelSelectors = [
    '[role="dialog"]',
    '[data-panel]',
    '[data-detail]',
    '[class*="detail-panel"]',
    '[class*="detail-panel"]',
    '[class*="drawer"]',
    '[class*="modal"]',
    '[class*="slide-over"]',
    '[class*="card"]',
    '[class*="sidebar"]',
    '[class*="panel"]',
    '[class*="view"]',
  ];

  for (const selector of panelSelectors) {
    const panels = document.querySelectorAll(selector);
    for (const panel of panels) {
      if (isElementHidden(panel)) continue;
      // Skip tiny panels (less than 100 chars of text content)
      // and huge panels (likely the whole page, not a detail view)
      const text = panel.textContent?.trim() || '';
      if (text.length > 100 && text.length < 5000) {
        // Additional check: panel should have label-value pairs or structured fields
        const hasLabelValuePairs = panel.querySelectorAll('label, dt, [class*="label"], [class*="field"]').length >= 2;
        const hasFormFields = panel.querySelectorAll('input, select, textarea').length >= 2;
        if (hasLabelValuePairs || hasFormFields) {
          return panel;
        }
      }
      // For panels with moderate content (20-100 chars), still consider them
      if (text.length > 20 && text.length <= 100) {
        return panel;
      }
    }
  }

  // Look for a detail section that's not the main table area
  const detailSections = document.querySelectorAll('[class*="detail"], [data-detail="true"]');
  for (const section of detailSections) {
    if (isElementHidden(section)) continue;
    return section;
  }

  return null;
}

/**
 * Extract record data from a detail panel.
 * Looks for label-value pairs (common pattern: label followed by value in flex/grid layouts).
 */
function extractRecordFromPanel(panel: Element): ActiveRecordSnapshot | null {
  const fields: Record<string, string> = {};

  // Strategy 1: Look for explicit label-value pairs (dt/dd, label + span, etc.)
  const definitionLists = panel.querySelectorAll('dl, [class*="field"], [class*="detail-row"]');
  for (const dl of definitionLists) {
    const dts = dl.querySelectorAll('dt, [class*="label"], [class*="field-label"], label');
    const dds = dl.querySelectorAll('dd, [class*="value"], [class*="field-value"], span');
    const labels = Array.from(dts).map(el => el.textContent?.trim() || '');
    const values = Array.from(dds).map(el => el.textContent?.trim() || '');
    const count = Math.min(labels.length, values.length);
    for (let i = 0; i < count; i++) {
      const label = labels[i];
      if (label) fields[label] = values[i]?.substring(0, MAX_CELL_LENGTH) || '';
    }
  }

  // Strategy 2: Look for div-based key-value pairs (common in detail views)
  if (Object.keys(fields).length === 0) {
    const rows = panel.querySelectorAll('[class*="grid"], [class*="flex"]');
    for (const row of rows) {
      const children = Array.from(row.children);
      if (children.length === 2) {
        const label = children[0]?.textContent?.trim() || '';
        const value = children[1]?.textContent?.trim() || '';
        if (label && value && label.length < 50) {
          fields[label] = value.substring(0, MAX_CELL_LENGTH);
        }
      }
    }
  }

  // Infer record type from the panel heading
  let type: string | undefined;
  const heading = panel.querySelector('h1, h2, h3, [class*="title"]');
  if (heading) {
    type = heading.textContent?.trim()?.substring(0, 30) || undefined;
  }

  // If we have structured fields, return them
  if (Object.keys(fields).length >= 2) {
    return { type, fields };
  }

  // Fallback: return raw text content of the panel (truncated)
  const rawText = panel.textContent?.trim()?.substring(0, 1000) || '';
  if (rawText.length > 20) {
    return { type, fields: {}, rawData: rawText };
  }

  return null;
}

// ──────────────────────────────────────────────
// React context for screen data (serializable metadata)
// ──────────────────────────────────────────────

const ScreenContext = createContext<{
  data: ScreenData | null;
  setScreenData: (data: ScreenData | null) => void;
}>({
  data: null,
  setScreenData: () => {},
});

export function ScreenContextProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<ScreenData | null>(null);
  const setScreenData = useCallback((newData: ScreenData | null) => {
    setData(newData ? { ...newData, updatedAt: Date.now() } : null);
  }, []);

  return (
    <ScreenContext.Provider value={{ data, setScreenData }}>
      {children}
    </ScreenContext.Provider>
  );
}

export function useScreenData() {
  return useContext(ScreenContext);
}

/**
 * Hook for pages to report their screen context to the AI copilot.
 * Automatically clears when the component unmounts.
 * Safe: only reports metadata (counts, filter values), never full document bodies.
 */
export function useReportScreen(
  summary: string,
  extra?: Omit<ScreenData, 'summary'>
) {
  const { setScreenData } = useScreenData();
  const stableExtraRef = useRef(extra);
  // Update the ref when extra changes, but don't trigger re-render
  stableExtraRef.current = extra;

  useEffect(() => {
    setScreenData({
      summary,
      ...stableExtraRef.current,
    });
    return () => {
      setScreenData(null);
    };
  }, [summary, setScreenData]);
}

/**
 * @deprecated Use data-ai-action attribute on buttons instead.
 * Kept for backward compatibility -- will be removed.
 */
export function useAIActionHandlers(
  _actions: { id: string; handler: () => void }[]
) {
  // No-op -- replaced by DOM actuator (data-ai-action)
}
